import { useForm } from 'react-hook-form';
import { UpdateReleaseVersionSongPatchData } from 'types/releases';
import { getValidDirtyValues } from 'app/utils/utils';
import { useAppDispatch } from 'app/store';
import { updateLabelVersionEffect } from 'app/store/Releases/effects';
import { useEffect } from 'react';
import {
  convertSongVersionFormToApiRequestData,
  getFieldFallback,
} from '../LabelVersionDetailsForm/utils';
import {
  LabelV2VersionAudioFormComponentProps,
  LabelV2VersionAudioFormData,
} from './types';

export const useLabelV2VersionAudio = ({
  releaseVersion,
  updateAudioValidations,
  primaryAudio,
  fetchAudioDetailsValidations,
  isAudioReplacing,
  isSyncing,
  primaryAudioFetching,
  audioData,
  disabledAudios,
  replaceAudioVersion,
  versionTypesDDL,
  updateVersion,
}: LabelV2VersionAudioFormComponentProps) => {
  const dispatch = useAppDispatch();

  const primaryAudioDisabled =
    isAudioReplacing || isSyncing || primaryAudioFetching;

  useEffect(() => {
    if (!updateAudioValidations && primaryAudio?.audioData.guid) {
      fetchAudioDetailsValidations(primaryAudio.audioData.guid);
    }
  }, [updateAudioValidations, primaryAudio, fetchAudioDetailsValidations]);

  const defaultValues: LabelV2VersionAudioFormData = {
    versionTitle: releaseVersion.metadata.versionTitle || '',
  };

  const form = useForm<LabelV2VersionAudioFormData>({
    defaultValues,
  });

  const {
    getValues,
    setValue,
    reset,
    formState: { errors, dirtyFields },
  } = form;

  const updateSongVersion = async (
    formData: UpdateReleaseVersionSongPatchData
  ) => {
    try {
      await dispatch(updateLabelVersionEffect({ formData })).unwrap();
    } catch {
      // proceed
    }
  };

  const onSubmit = async () => {
    const data = getValues();
    const validDirtyValues = getValidDirtyValues<LabelV2VersionAudioFormData>(
      errors,
      dirtyFields,
      data
    );

    if (Object.keys(validDirtyValues).length) {
      reset(data, { keepErrors: true });
      await updateSongVersion(
        convertSongVersionFormToApiRequestData(
          validDirtyValues,
          releaseVersion,
          {}
        )
      );
    }
  };

  const fallbackTextFieldToSnapshot = (name) => (event) => {
    if (event.target.value === '') {
      setValue(name, getFieldFallback(name, releaseVersion));
    }
    onSubmit();
  };

  return {
    form,
    onSubmit,
    isSyncing,
    primaryAudio,
    audioData,
    disabledAudios,
    releaseVersion,
    primaryAudioDisabled,
    replaceAudioVersion,
    primaryAudioFetching,
    isAudioReplacing,
    versionTypesDDL,
    updateVersion,
    fallbackTextFieldToSnapshot,
  };
};
